/**
 * A document's page material, fetched when — and only when — it is played.
 *
 * The post carries the page table, so a feed row can draw every badge without
 * asking for anything; this is the other half of that bargain, and it is the half
 * that costs bytes. A clip and five questions per page is a lot to hand somebody
 * who is scrolling past, so the fetch is `enabled` from the play, not from the
 * render — a document that is read but never played makes no request at all.
 *
 * Read from the PDS by AT URI rather than from the AppView, for the reason the
 * rest of the app reads records: the post names an address and this is what lives
 * at it. It also means the feature works on a deployment whose AppView has not
 * been taught about material, which is exactly the deployment it is launching on.
 *
 * Two things this reader does not swallow.
 *
 *  - **A fetch the app is sitting on.** react-query's default is to *pause* a
 *    request while the app believes it is offline, and this app's belief comes
 *    from a health check against the public AppView (`#/lib/react-query`) — which
 *    says nothing about whether a record on the reader's own PDS can be read. A
 *    paused fetch is indistinguishable from a slow one from the outside: a
 *    spinner over a page, waiting for a request that was never sent. So this one
 *    is read wherever the app thinks it stands.
 *
 *  - **A failure that is not an answer.** "The record is not there" is an answer,
 *    and it is permanent — material is written with its post and never edited —
 *    so a reader is told it. A connection that did not get through is not an
 *    answer, and caching one as "this document has no questions", forever (see
 *    `staleTime`), is how a bad minute becomes a document that can never be
 *    played.
 *
 *  - **Which service answers, when nobody is signed in.** The agent the app hands a
 *    signed-out reader is pointed at the **public AppView** (`createPublicAgent` in
 *    `#/state/session/agent`), and the AppView answers `com.atproto.repo.getRecord` out of
 *    its *own index* — which holds no rows for `app.bsky.feed.documentMaterial`, a collection
 *    this deployment stores without a lexicon. So it refuses with `RecordNotFound`, and
 *    `RecordNotFound` is this layer's word for "there is nothing to play": a signed-out reader
 *    who pressed play on a document that *does* ask something was told it asks nothing — the
 *    card advertised questions off the post's own page table, the turn was held open for them,
 *    and no card ever came. The record's home is the **PDS** the app is built against, the same
 *    host the document's blob already comes from (`#/lib/document/util`), which is why the pages
 *    rendered while the questions vanished; so a read with no session is made there
 *    (`materialReadAgent`), and a session's read is left exactly as it was — the account's own
 *    agent asks the service it signed in against.
 */
import {AtpAgent, AtUri} from '@atproto/api'
import {useQuery} from '@tanstack/react-query'

import {BSKY_SERVICE} from '#/lib/constants'
import {
  type DocumentMaterial,
  type DocumentMaterialRef,
  materialRefFromRecord,
  normalizeDocumentMaterial,
} from '#/lib/document/material'
import {isRecordNotFound} from '#/lib/strings/errors'
import {createQueryKey} from '#/state/queries/util'
import {useAgent} from '#/state/session'

const documentMaterialQueryKeyRoot = 'documentMaterial'

export const createDocumentMaterialQueryKey = (materialUri: string) =>
  createQueryKey(documentMaterialQueryKeyRoot, {materialUri})

/**
 * Where a post says its material is *now*, read from the post's own record.
 *
 * A card is handed a material address off a **cached post record**
 * (`materialRefFromRecord` in `#/lib/document/material`) and that copy is what a retry
 * used to ask at. Two bad minutes are not the same failure, and only one of them is
 * cured by asking the same address again:
 *
 *  - **The wire dropped.** Nothing is wrong with the address; asking it once more is
 *    exactly right, and this read changes nothing about it.
 *  - **The address is not the one the post names.** The copy in hand was taken when the
 *    post was last read, and the deployment that serves it may since have been
 *    repaired, re-indexed or rewritten — a record fixed at the source, an embed put
 *    right, a post that came off a cache older than its own material. No number of
 *    retries at a stale address can find what is at the current one, so a retry that
 *    only repeats is a press that cannot recover without a reload.
 *
 * So the first move of a retry is this read: the *post*, at its own AT URI, and the
 * address it names is what the material read then asks for. It is the same read the
 * material fetch itself is — `getRecord` against the PDS the address names — for the
 * same reason, so this keeps working on a deployment whose AppView has not been taught
 * about material.
 *
 * `undefined` is the post saying it names no material: a document with nothing on its
 * pages, and equally a record repaired to hold nothing. A read that *fails* is thrown,
 * because a caller that could not reach the post has learned nothing about the
 * material either — and a caller holding an address already has something to ask.
 */
export async function deriveDocumentMaterialRef({
  agent,
  postUri,
}: {
  agent: AtpAgent
  postUri: string
}): Promise<DocumentMaterialRef | undefined> {
  const atUri = new AtUri(postUri)
  const res = await agent.com.atproto.repo.getRecord({
    repo: atUri.hostname,
    collection: atUri.collection,
    rkey: atUri.rkey,
  })
  return materialRefFromRecord(res.data.value)
}

/**
 * A signed-out reader's own record reader: the PDS, with no session attached.
 *
 * One agent for every guest read rather than one per query, and built on first use rather than at
 * module scope, so importing this file costs nothing and a deployment that never has a signed-out
 * reader never builds it. It is deliberately not a sibling of `createPublicAgent` in
 * `#/state/session/agent`: that factory exists for the *app*'s guest browsing — the AppView, the
 * right service for posts, feeds and profiles — while this one exists for the one read whose
 * record the AppView cannot hold (see the module note).
 */
let guestRecordReader: AtpAgent | undefined

function pdsRecordReader(): AtpAgent {
  if (!guestRecordReader) {
    guestRecordReader = new AtpAgent({service: BSKY_SERVICE})
  }
  return guestRecordReader
}

/**
 * The agent a document's material is read with.
 *
 * The choice is the whole of the fix for the signed-out card that advertised questions nobody
 * could be served. **An agent that holds a session asks the account's own service** — the PDS it
 * signed in against, which is where the record is, exactly as before — and **an agent that holds
 * none is the guest's**, pointed at the public AppView, whose index has no rows for
 * `app.bsky.feed.documentMaterial` and whose `RecordNotFound` the layer above reads as "this
 * document asks nothing"; so a signed-out read is made at the PDS itself.
 *
 * The flag is the agent's own (`AtpAgent.hasSession`), which is the same fact as the app's — the
 * reducer hands out a session-less public agent the moment there is nothing signed in — and
 * taking it off the agent keeps the choice a function of the one thing a test can hand over: an
 * agent, and the service it would ask.
 */
export function materialReadAgent(sessionAgent: AtpAgent): AtpAgent {
  return sessionAgent.hasSession ? sessionAgent : pdsRecordReader()
}

/**
 * The material at `materialUri`, read with `agent` — `null` when there is none at that address.
 *
 * "None" is an answer, not a failure: a record that is not there, and one whose
 * pages normalize to nothing, both mean there is nothing to play. Every other
 * refusal is thrown, because it is not an answer (see the module note).
 */
export async function readDocumentMaterialRecord({
  agent,
  materialUri,
}: {
  agent: AtpAgent
  materialUri: string
}): Promise<DocumentMaterial | null> {
  const atUri = new AtUri(materialUri)
  try {
    const res = await agent.com.atproto.repo.getRecord({
      repo: atUri.hostname,
      collection: atUri.collection,
      rkey: atUri.rkey,
    })
    /*
     * The author's DID, so a page whose clip is named only by its blob gets
     * the one address that can be fetched from it: the record's own repo, at
     * the PDS that serves it.
     */
    return (
      normalizeDocumentMaterial(res.data.value, {did: atUri.hostname}) ?? null
    )
  } catch (e) {
    /*
     * A record that is not there is the answer "there is nothing to play" —
     * the only refusal that is one, and the one worth remembering: above, a
     * record whose pages normalize to nothing is answered the same way, since
     * a reader who opens the questions on a page with none should see no card
     * rather than an empty one.
     */
    if (isRecordNotFound(e)) return null
    // Anything else is thrown, so it is retried and never cached as an answer.
    throw e
  }
}

/**
 * The material at `materialUri` — `null` when there is none at that address.
 *
 * The agent is chosen *outside* the query (`materialReadAgent`): a reader who signs in while a
 * read is pending re-renders this hook, and the ask that follows goes to their own service. The
 * query key does not move, which is right rather than careless — material is immutable, so an
 * answer already in hand is the answer whichever service delivered it.
 */
export function useDocumentMaterialQuery(
  materialUri: string | undefined,
  {enabled = true}: {enabled?: boolean} = {},
) {
  const agent = materialReadAgent(useAgent())
  return useQuery({
    queryKey: createDocumentMaterialQueryKey(materialUri ?? ''),
    enabled: !!materialUri && enabled,
    queryFn: () =>
      readDocumentMaterialRecord({agent, materialUri: materialUri!}),
    // Material is written once, with the post, and never edited afterwards.
    staleTime: Infinity,
    retry: 1,
    /*
     * Read even while the app believes it is offline: the record this asks for is
     * on the PDS the post names — the reader's own host in dev — and the app's
     * online state is a health check against a *different* service that can be
     * unreachable for reasons that have nothing to do with this document. A
     * paused request is the one failure a reader cannot see the end of.
     */
    networkMode: 'always',
  })
}
